import React from 'react';
import { Cluster } from '../hooks/useClusters';
import { Button } from '../../../ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faMapMarkerAlt, faCalendar } from '@fortawesome/free-solid-svg-icons';

interface ClusterDetailPanelProps {
    cluster: Cluster | null;
    onFetch?: (id: string) => void;
    onRefresh?: (id: string) => void;
    onSuggestSplit?: (id: string) => void;
    isFetching?: boolean;
}

const ClusterDetailPanel: React.FC<ClusterDetailPanelProps> = ({
    cluster,
    onFetch,
    onRefresh,
    onSuggestSplit,
    isFetching = false
}) => {
    if (!cluster) {
        return (
            <div className="p-4 text-center text-muted-foreground text-sm">
                Select a cluster to view details
            </div>
        );
    }

    const lastFetched = cluster.lastFetchedAt
        ? new Date(cluster.lastFetchedAt).toLocaleDateString()
        : 'Never';
    const isStale = cluster.nextRefreshAfter && new Date(cluster.nextRefreshAfter) < new Date();

    return (
        <div className="p-4 space-y-4">
            <div>
                <h3 className="font-semibold text-lg">{cluster.name}</h3>
                <p className="text-sm text-muted-foreground">{cluster.city}, {cluster.country}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faStore} className="h-3 w-3 text-muted-foreground" />
                    <span>{cluster.totalShops} shops</span>
                </div>
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="h-3 w-3 text-muted-foreground" />
                    <span>{cluster.h3Cells?.length || 0} cells</span>
                </div>
                <div className="flex items-center gap-2 col-span-2">
                    <FontAwesomeIcon icon={faCalendar} className="h-3 w-3 text-muted-foreground" />
                    <span>Last fetched: {lastFetched}</span>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {onFetch && (
                    <Button
                        size="sm"
                        variant={cluster.status === 'draft' ? 'default' : 'outline'}
                        onClick={() => onFetch(cluster._id)}
                        disabled={isFetching}
                        title={cluster.status === 'draft' ? 'Fetch shops from Google Places' : 'Add new shops from Google Places'}
                    >
                        {isFetching ? 'Fetching...' : cluster.status === 'draft' ? 'Fetch shops' : 'Fetch from Google'}
                    </Button>
                )}
                {onRefresh && cluster.status === 'active' && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRefresh(cluster._id)}
                        disabled={isFetching}
                        title={isStale ? 'Cluster is stale – refresh from Google' : 'Force refresh from Google Places'}
                    >
                        {isFetching ? 'Refreshing...' : isStale ? 'Refresh (stale)' : 'Refresh'}
                    </Button>
                )}
                {cluster.needsSplit && onSuggestSplit && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onSuggestSplit(cluster._id)}
                    >
                        Suggest split
                    </Button>
                )}
            </div>
        </div>
    );
};

export default ClusterDetailPanel;
